import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, apiPost } from '../lib/api';
import { PageTitle, Card, Btn, Section, Kpi, Pill } from '../components/ui-kit';

interface CraEntry { date: string; hours: number; comment?: string; }

export default function MissionCRA() {
  const { id } = useParams();
  const { getToken } = useXyqarAuth();
  const [entries, setEntries] = React.useState<CraEntry[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<{ entries: CraEntry[] }>(`/api/v1/client/missions/${id}/cra`, tok);
        setEntries(j.entries ?? []);
      } catch { /* empty */ }
    })();
  }, [id, getToken]);

  const total = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const commentedEntries = entries.filter((entry) => entry.comment && entry.comment.trim().length > 0).length;

  const validate = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      const tok = await getToken().catch(() => '');
      await apiPost(`/api/v1/client/missions/${id}/cra/approve`, {}, tok);
      setMsg('CRA approved · invoice generated.');
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle kicker={`Mission · ${id}`} title="CRA · Compte rendu d'activité" />

      <Section title="Review summary" hint="Approval triggers the downstream client-side CRA approval endpoint.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Entries" value={entries.length} />
          <Kpi accent="green" label="Total hours" value={`${total}h`} />
          <Kpi label="Commented lines" value={commentedEntries} />
          <Kpi accent={entries.length > 0 ? 'amber' : undefined} label="Approval state" value={entries.length > 0 ? 'Ready' : 'Waiting'} />
        </div>
      </Section>

      <Section title="Validation rules">
        <div className="grid gap-4 lg:grid-cols-[1.35fr,0.65fr]">
          <Card>
            <p className="text-sm text-white">Review hours, comments, and weekly completeness before approval.</p>
            <p className="text-xs text-white/60 mt-2 leading-relaxed">
              Once approved, the CRA enters the downstream invoicing flow. Use this page as the client-side validation checkpoint, not as an editing surface.
            </p>
          </Card>
          <Card>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Status</p>
              <Pill tone={entries.length > 0 ? 'warn' : 'mute'}>{entries.length > 0 ? 'awaiting approval' : 'no entries'}</Pill>
            </div>
          </Card>
        </div>
      </Section>

      <Section title="CRA ledger">
        <Card>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">
              <tr><th className="text-left py-3">Date</th><th className="text-right py-3">Hours</th><th className="text-left py-3 pl-6">Comment</th></tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-white/40">No entries yet — talent submits weekly.</td></tr>}
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-3 font-mono text-white">{e.date}</td>
                  <td className="py-3 text-right text-white">{e.hours}h</td>
                  <td className="py-3 pl-6 text-white/60">{e.comment ?? '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/15"><td className="py-3 text-white/40 text-xs uppercase tracking-wider">Total</td><td className="py-3 text-right text-white font-bold">{total}h</td><td /></tr>
            </tfoot>
          </table>

          {msg && <p className="text-xs text-white/60 mt-4">{msg}</p>}
          <div className="flex justify-end mt-6">
            <Btn primary onClick={validate} disabled={submitting || entries.length === 0}>
              {submitting ? 'Approving…' : 'Approve CRA →'}
            </Btn>
          </div>
        </Card>
      </Section>
    </div>
  );
}
